import customerRideApi from 'api/customerRideApi';
import ToastHelper from 'general/helpers/ToastHelper';
import React, { useState } from 'react';
import RideAppIcon from './RideAppIcon';
import RidePortal from './RidePortal';
import {
  formatRideDateTime,
  formatRideMoney,
  formatRideAddress,
  rideErrorMessage,
} from '../rideUtils';

const statusMeta = (status) => ({
  OPEN: { label: 'Đang tìm tài xế', tone: 'warning' },
  AWAITING_PAYMENT: { label: 'Chờ thanh toán', tone: 'warning' },
  MATCHED: { label: 'Đã ghép tài xế', tone: 'success' },
  COMPLETED: { label: 'Đã hoàn thành', tone: 'success' },
  FINISHED: { label: 'Đã hoàn thành', tone: 'success' },
  CANCELLED: { label: 'Đã hủy', tone: 'neutral' },
  EXPIRED: { label: 'Đã hết hạn', tone: 'neutral' },
  PAYMENT_EXPIRED: { label: 'Hết hạn thanh toán', tone: 'neutral' },
}[String(status || '').toUpperCase()] || { label: status || 'Yêu cầu hẹn giờ', tone: 'neutral' });

const paymentLabel = (method) => ({
  BIDV: 'Chuyển khoản BIDV',
  WALLET: 'Ví Xe ghép',
  CASH: 'Tiền mặt',
}[String(method || '').toUpperCase()] || method || 'Tiền mặt');

const RideDemandDetailModal = ({ demand, onClose, onChanged }) => {
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const meta = statusMeta(demand?.status);
  const fare = demand?.fare;
  const canCancel = demand?.status === 'OPEN';
  const pickup = formatRideAddress(demand?.pickup?.address);
  const dropoff = formatRideAddress(demand?.dropoff?.address);
  const payable = Math.max(0, Number(fare?.totalFare || 0) - Number(fare?.promoDiscountAmount || 0));

  const handleCopy = (text, label) => {
    if (!text) return;
    try {
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(String(text));
      } else {
        const ta = document.createElement('textarea');
        ta.value = String(text);
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      ToastHelper.showSuccess(`Đã sao chép ${label || 'thông tin'}!`);
    } catch (_) {
      ToastHelper.showSuccess(`Đã sao chép ${label || 'thông tin'}!`);
    }
  };

  const cancel = async () => {
    setWorking(true);
    setError('');
    try {
      await customerRideApi.cancelScheduledDemand(demand.demandId, 'Khách hàng hủy yêu cầu từ web');
      setShowCancelConfirm(false);
      ToastHelper.showSuccess('Đã hủy yêu cầu chuyến hẹn giờ.');
      onChanged();
    } catch (requestError) {
      setError(rideErrorMessage(requestError));
    } finally {
      setWorking(false);
    }
  };

  return (
    <RidePortal>
      <div className="customer-ride-modal-backdrop" role="presentation" onMouseDown={onClose}>
        <div
          className="customer-ride-modal customer-ride-detail-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Chi tiết yêu cầu chuyến đi"
          onMouseDown={(event) => event.stopPropagation()}
        >
          {/* 1. Modal Top Bar */}
          <div className="customer-ride-modal-head-bar">
            <div>
              <span className="customer-ride-eyebrow">YÊU CẦU CHUYẾN ĐI</span>
              <h2 className="modal-title">Chi tiết yêu cầu chuyến</h2>
            </div>
            <button
              className="customer-ride-modal-close-btn"
              type="button"
              onClick={onClose}
              aria-label="Đóng"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>

          <div className="customer-ride-modal-scroll-body">
            {/* 2. Status Banner */}
            <div className="customer-ride-status-banner">
              <div className="status-banner-left">
                <span className={`status-pill ${meta.tone}`}>
                  <span className="status-dot" />
                  {meta.label}
                </span>
                <span className="demand-type-tag">
                  <i className="fa-regular fa-clock" /> Chuyến hẹn giờ
                </span>
              </div>
              {demand?.matchedTripCode ? (
                <span className="matched-trip-badge">
                  <i className="fa-solid fa-car" /> Mã chuyến: {demand.matchedTripCode}
                </span>
              ) : (
                <span className="demand-date-label">
                  Khởi hành: {formatRideDateTime(demand?.departureFrom)}
                </span>
              )}
            </div>

            {/* 3. Route Card */}
            <div className="customer-ride-section-card route-card">
              <div className="section-card-title">
                <i className="fa-solid fa-route" /> Lộ trình di chuyển
              </div>
              <div className="route-timeline">
                <div className="route-stop pickup-stop">
                  <div className="stop-marker pickup-marker">
                    <i className="fa-regular fa-circle-dot" />
                  </div>
                  <div className="stop-info">
                    <span className="stop-label">Điểm đón</span>
                    <strong className="stop-primary">{pickup.primary}</strong>
                    {pickup.secondary && <span className="stop-secondary">{pickup.secondary}</span>}
                  </div>
                </div>
                <div className="route-connector-line" />
                <div className="route-stop dropoff-stop">
                  <div className="stop-marker dropoff-marker">
                    <i className="fa-solid fa-location-dot" />
                  </div>
                  <div className="stop-info">
                    <span className="stop-label">Điểm trả</span>
                    <strong className="stop-primary">{dropoff.primary}</strong>
                    {dropoff.secondary && <span className="stop-secondary">{dropoff.secondary}</span>}
                  </div>
                </div>
              </div>
            </div>

            {/* 4. Trip Specs Card */}
            <div className="customer-ride-section-card specs-card">
              <div className="section-card-title">
                <i className="fa-solid fa-circle-info" /> Chi tiết chuyến đi
              </div>
              <div className="specs-list">
                <div className="spec-item">
                  <span className="spec-label">Dịch vụ</span>
                  <span className="spec-value">{demand?.isCharter ? 'Bao xe EVG' : 'Xe ghép EVG'}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Số ghế đặt</span>
                  <span className="spec-value bold">
                    {demand?.isCharter
                      ? `${demand?.charterVehicleCapacity || 4} chỗ (Bao xe)`
                      : `${demand?.seats || 1} ghế`}
                  </span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Thời điểm đón dự kiến</span>
                  <span className="spec-value">{formatRideDateTime(demand?.departureFrom)}</span>
                </div>
                {demand?.departureTo && (
                  <div className="spec-item">
                    <span className="spec-label">Khung giờ đến dự kiến</span>
                    <span className="spec-value">{formatRideDateTime(demand.departureTo)}</span>
                  </div>
                )}
                <div className="spec-item">
                  <span className="spec-label">Phương thức thanh toán</span>
                  <span className="spec-value">{paymentLabel(demand?.paymentMethod)}</span>
                </div>
                <div className="spec-item spec-with-copy">
                  <span className="spec-label">Mã yêu cầu</span>
                  <div className="spec-copy-val">
                    <code className="demand-id-code">{demand?.demandId}</code>
                    <button
                      type="button"
                      className="mini-copy-btn"
                      onClick={() => handleCopy(demand?.demandId, 'Mã yêu cầu')}
                    >
                      <i className="fa-regular fa-copy" /> Sao chép
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* 5. Fare / Pricing Card */}
            {fare && (
              <div className="customer-ride-section-card fare-card">
                <div className="section-card-title">
                  <i className="fa-solid fa-receipt" /> Cước, phí chuyến đi
                </div>
                <div className="fare-breakdown">
                  <div className="fare-row">
                    <span>Giá cước dự kiến</span>
                    <span>{formatRideMoney(fare.totalFare, fare.currency)}</span>
                  </div>
                  {fare.promoCode && fare.promoDiscountAmount > 0 && (
                    <div className="fare-row promo-row">
                      <span>Mã khuyến mại "{fare.promoCode}"</span>
                      <span className="discount-val">
                        -{formatRideMoney(fare.promoDiscountAmount, fare.currency)}
                      </span>
                    </div>
                  )}
                  <div className="fare-divider" />
                  <div className="fare-row total-row">
                    <strong>Tổng tiền thanh toán</strong>
                    <strong className="total-amount">
                      {formatRideMoney(payable, fare.currency)}
                    </strong>
                  </div>
                </div>
              </div>
            )}

            {/* Note & Cancellation Reason */}
            {demand?.note && (
              <div className="customer-ride-note-box">
                <div className="note-title">
                  <i className="fa-regular fa-message" /> Ghi chú cho tài xế
                </div>
                <p>{demand.note}</p>
              </div>
            )}
            {demand?.cancellationReason && (
              <div className="customer-ride-cancel-reason-box">
                <div className="reason-title">
                  <i className="fa-solid fa-triangle-exclamation" /> Lý do hủy chuyến
                </div>
                <p>{demand.cancellationReason}</p>
              </div>
            )}

            {error && (
              <div className="customer-ride-alert danger">
                <i className="fa-solid fa-circle-exclamation" /> {error}
              </div>
            )}
          </div>

          {/* 6. Footer Actions */}
          <div className="customer-ride-modal-footer">
            {canCancel && (
              <button
                className="cancel-demand-btn"
                type="button"
                disabled={working}
                onClick={() => {
                  setError('');
                  setShowCancelConfirm(true);
                }}
              >
                {working ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin" /> Đang hủy...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-ban" /> Hủy yêu cầu chuyến
                  </>
                )}
              </button>
            )}
            <button className="close-demand-btn" type="button" onClick={onClose}>
              Đóng
            </button>
          </div>

          {showCancelConfirm && (
            <div
              className="customer-ride-confirm-backdrop"
              role="presentation"
              onMouseDown={(event) => {
                event.stopPropagation();
                if (event.target === event.currentTarget && !working) setShowCancelConfirm(false);
              }}
            >
              <div
                className="customer-ride-confirm-dialog"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="cancel-demand-title"
                aria-describedby="cancel-demand-description"
                onMouseDown={(event) => event.stopPropagation()}
              >
                <button
                  type="button"
                  className="customer-ride-confirm-close"
                  aria-label="Đóng thông báo"
                  disabled={working}
                  onClick={() => setShowCancelConfirm(false)}
                >
                  <i className="fa-solid fa-xmark" />
                </button>
                <div className="customer-ride-confirm-icon" aria-hidden="true">
                  <i className="fa-solid fa-triangle-exclamation" />
                </div>
                <div className="customer-ride-confirm-content">
                  <span>HỦY YÊU CẦU CHUYẾN</span>
                  <h3 id="cancel-demand-title">Bạn muốn hủy chuyến này?</h3>
                  <p id="cancel-demand-description">
                    Hệ thống sẽ dừng tìm tài xế cho yêu cầu này. Thao tác hủy không thể hoàn tác.
                  </p>
                </div>
                <div className="customer-ride-confirm-trip">
                  <i className="fa-regular fa-clock" />
                  <div>
                    <small>Thời gian đón dự kiến</small>
                    <strong>{formatRideDateTime(demand?.departureFrom)}</strong>
                  </div>
                </div>
                {error && (
                  <div className="customer-ride-confirm-error">
                    <i className="fa-solid fa-circle-exclamation" /> {error}
                  </div>
                )}
                <div className="customer-ride-confirm-actions">
                  <button
                    type="button"
                    className="keep-demand-btn"
                    disabled={working}
                    onClick={() => setShowCancelConfirm(false)}
                  >
                    Giữ chuyến
                  </button>
                  <button
                    type="button"
                    className="confirm-cancel-demand-btn"
                    disabled={working}
                    onClick={cancel}
                  >
                    {working ? (
                      <><i className="fa-solid fa-spinner fa-spin" /> Đang hủy...</>
                    ) : (
                      <><i className="fa-regular fa-circle-xmark" /> Xác nhận hủy</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </RidePortal>
  );
};

export default RideDemandDetailModal;
